import { ADD_PROPOSAL_INFO, ADD_PROPOSAL_STEPS } from '@/store/modules/ProposalContext';
import store from '@/store/store';
import { isDevelop } from '@/services/environmentService';
import { formatter } from '@/services/FormatService';
import {
	generalMapField,
	healthCheck,
	mapApiToModel,
	mapProductClaims,
	mapProductHighlightField,
	moveReadonlyFieldsToTop,
	preProcessCheckFields,
	preProcessFieldsByCustomerType,
} from '@/services/FieldService';
import { DigitalAcceptCmsPage } from '@/types/types';
import { AcceptPage } from '@/cms/definitions/content-types';
import {
	getModifiedCmsPolicy,
	handlePayment,
	handleTermination,
	setReplacements,
	simplifyInputFields,
} from '@/services/PageService';
import { POLICY_FIELDS, PROPOSAL_STEPS, UNDERWRITING_QUESTIONS_FIELDS, UNDERWRITING_TEXT_FIELDS } from '@/types/enums';
import { normalizeModelFromApi } from '@/services/ApiNormalize';

export default class ModelMapper {
	private readonly proposal: DigitalAcceptCmsPage;
	private readonly cmsModel: AcceptPage;
	private readonly replacements: Map<string, string> = new Map();

	constructor(proposal: any, cmsModel: any) {
		this.proposal = proposal.data;
		this.cmsModel = cmsModel;
		setReplacements(this.cmsModel, this.proposal, this.replacements);

		normalizeModelFromApi(this.proposal, this.cmsModel);

		this.init();
	}

	private init() {
		store.dispatch(ADD_PROPOSAL_INFO, { proposal: this.proposal, cmsModel: this.cmsModel });
	}

	public async mapProposalSteps() {
		const proposalSteps = [];

		// this is the order in which steps are shown
		this.mapPolicies(proposalSteps);
		this.mapPreviousInsuranceStep(proposalSteps);
		// !WAIT FOR SalesForce - Markedsføring samtykke - not in use in first release
		// this.mapConsentStep(proposalSteps);
		this.mapSummaryStep(proposalSteps);
		await store.dispatch(ADD_PROPOSAL_STEPS, proposalSteps);
	}

	private mapPolicies(proposalSteps) {
		const removeFields = [];
		this.proposal.policies.forEach((policy, inx) => {
			const cmsPolicy = getModifiedCmsPolicy(this.cmsModel, policy.type);
			cmsPolicy.id = policy.id;
			let previousFieldId: string;
			if (cmsPolicy.inputFields) {
				preProcessCheckFields(cmsPolicy.inputFields);
				preProcessFieldsByCustomerType(cmsPolicy.inputFields);

				cmsPolicy.inputFields.forEach((cmsField) => {
					if (cmsField.inactive) {
						console.warn('remove inactive CMSfield', cmsField.id, cmsField);
						removeFields.push(cmsField.id);
					} else {
						cmsField.isValid = false;
						const apiField = policy.policy_object.hasOwnProperty(cmsField.id);
						if (apiField) {
							mapApiToModel(cmsField, policy.policy_object[cmsField.id]);
						} else {
							// set readonly false if field is not in API
							cmsField.readonly = false;
						}

						generalMapField(cmsPolicy, cmsField, previousFieldId, inx, this.replacements);
						if (!cmsField.hidden) {
							previousFieldId = cmsField.id;
						}
					}
				});

				cmsPolicy.inputFields = cmsPolicy.inputFields.filter((field) => !removeFields.includes(field.id));
			} else {
				cmsPolicy.inputFields = [];
			}

			handleTermination(cmsPolicy.id, cmsPolicy.inputFields, policy);

			handlePayment(cmsPolicy.inputFields);

			const frequency = policy[POLICY_FIELDS.payment_frequency];

			let priceFrequencyText = 'år';
			switch (frequency) {
				case 2:
					priceFrequencyText = '6md.';
					break;
				case 4:
					priceFrequencyText = '3md.';
					break;
				case 12:
					priceFrequencyText = 'md.';
					break;
			}

			cmsPolicy.priceDisplay = `${formatter.format(policy.price / frequency)} kr./${priceFrequencyText}`;
			cmsPolicy.yearlyPrice = policy.price;
			cmsPolicy.title = policy.title;
			cmsPolicy.description = policy.description;
			cmsPolicy.buttonLabel = this.cmsModel.adjustPoliciesBlock.buttonLabel;
			cmsPolicy.buttonBackLabel = this.cmsModel.adjustPoliciesBlock.buttonBackLabel;
			if (cmsPolicy.moveReadonlyFieldsToTop) {
				moveReadonlyFieldsToTop(cmsPolicy);
			}

			proposalSteps.push(cmsPolicy);

			if (isDevelop) {
				healthCheck(policy, cmsPolicy);
			}
		});
	}

	private mapPreviousInsuranceStep(proposalSteps: Array<any>): void {
		// map underwriting questions if any
		const inputFields = this.cmsModel.previousInsuranceBlock.inputFields;
		for (const question of Object.values(UNDERWRITING_QUESTIONS_FIELDS)) {
			const cmsField = inputFields?.items?.find((field) => field.content.id === question);
			if (cmsField && this.proposal?.underwriting_questions?.hasOwnProperty(question)) {
				cmsField.content.isValid = true;
				cmsField.content.value = this.proposal.underwriting_questions[question] + '';
			} else {
				cmsField.content.hidden = true;
			}
		}

		if (this.proposal.underwriting_questions === undefined) {
			// no total claim value - hide the total claims text field
			const totalClaimsTextField = inputFields?.items?.find(
				(field) => field.content.id === UNDERWRITING_TEXT_FIELDS.total_claims_text
			);
			if (totalClaimsTextField) {
				totalClaimsTextField.content.hidden = true;
			}
		}

		const step = this.mapStep(
			this.cmsModel.previousInsuranceBlock,
			PROPOSAL_STEPS.previousBlock,
			this.cmsModel.previousInsuranceBlock.buttonLabel,
			this.cmsModel.previousInsuranceBlock.buttonBackLabel
		);

		mapProductClaims(step, this.cmsModel);

		proposalSteps.push(step);
	}

	private mapConsentStep(proposalSteps: Array<any>): void {
		const step = this.mapStep(
			this.cmsModel.consentsBlock,
			PROPOSAL_STEPS.consentBlock,
			this.cmsModel.consentsBlock.buttonLabel,
			this.cmsModel.consentsBlock.buttonBackLabel
		);
		proposalSteps.push(step);
	}

	private mapSummaryStep(proposalSteps: Array<any>): void {
		const step = this.mapStep(
			this.cmsModel.summaryBlock,
			PROPOSAL_STEPS.summaryBlock,
			this.cmsModel.summaryBlock.buttonLabel,
			this.cmsModel.summaryBlock.buttonBackLabel
		);

		proposalSteps.push(step);
	}

	public getReceiptPolicyDetails(): any {
		const step = this.mapStep(
			this.cmsModel,
			PROPOSAL_STEPS.receiptBlock,
			this.cmsModel.buttonLabel,
			this.cmsModel.buttonBackLabel
		);

		mapProductHighlightField(step, true);
		return step;
	}

	private mapStep(cmsBlock, blockId, buttonLabel, buttonBackLabel) {
		const step: any = {};
		step.title = cmsBlock.stepHeadline;
		simplifyInputFields(cmsBlock);

		let previousFieldId: string;
		const removeFields = [];

		cmsBlock.inputFields.forEach((field, inx) => {
			if (field.inactive) {
				console.warn('remove Inactive CMSfield', field.id);
				removeFields.push(field.id);
			} else {
				generalMapField(cmsBlock, field, previousFieldId, inx, this.replacements);
				if (!field.hidden) {
					previousFieldId = field.id;
				}
			}
		});
		cmsBlock.inputFields = cmsBlock.inputFields.filter((field) => !removeFields.includes(field.id));

		step.inputFields = cmsBlock.inputFields;
		step.hasMasterChildCheckFields = cmsBlock.hasMasterChildCheckFields;
		step.maximizeWidth = cmsBlock.maximizeWidth;

		// missing values
		step.id = blockId;
		step.buttonLabel = buttonLabel;
		step.buttonBackLabel = buttonBackLabel;
		return step;
	}
}
